import { useEffect, useState } from "react";
import { Download, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;
const STATUS_CLS = { open: "bg-amber-500/20 text-amber-300", paid: "bg-[#83B68B]/20 text-[#83B68B]", waived: "bg-white/10 opacity-70" };

export function FinesLog({ compact = false }) {
  const [data, setData] = useState(null);
  const [season, setSeason] = useState("");
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/finance/fines", { params: season ? { season } : {} }).then(r => setData(r.data)).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [season]); // eslint-disable-line
  const setStatus = async (f, status) => {
    const ref = status === "paid" ? window.prompt("Payment reference (optional)", f.payment_reference || "") : null;
    if (status === "paid" && ref === null) return;
    try { await api.patch(`/finance/fines/${f.id}`, { status, payment_reference: ref || null }); toast.success(`Fine marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const csv = async () => {
    try {
      const { data: blob } = await api.get("/finance/fines.csv", { params: season ? { season } : {}, responseType: "blob" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `league-fines${season ? `-${season.replace("/", "-")}` : ""}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  return (
    <div className="space-y-4" data-testid="fines-log">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {!compact && <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Payments</div>}
          <h2 className={`font-display font-black ${compact ? "text-xl" : "text-3xl"} mt-1`}>League fines</h2>
          <p className="opacity-60 text-sm mt-1">Fines approved from the Club Inbox land here automatically; log others by hand. Marking paid completes the linked task.</p>
        </div>
        <div className="flex gap-2">
          <select value={season} onChange={e => setSeason(e.target.value)} data-testid="fines-season" className="bg-background border border-border rounded px-2 py-1.5 text-sm">
            <option value="">All seasons</option>
            {(data?.seasons || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button variant="secondary" size="sm" onClick={csv} data-testid="fines-csv"><Download className="w-4 h-4 mr-1" />CSV</Button>
          <Button size="sm" onClick={() => setAdding(true)} data-testid="fines-add-btn"><Plus className="w-4 h-4 mr-1" />Log fine</Button>
        </div>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-3" data-testid="fines-totals">
          <Stat label="Open" value={gbp(data.open_total)} testid="fines-open-total" accent />
          <Stat label="Paid" value={gbp(data.paid_total)} testid="fines-paid-total" />
          <Stat label="Fines" value={data.count} testid="fines-count" />
        </div>
      )}
      {data && data.items.length === 0 && <div className="text-sm opacity-60 border border-dashed border-border rounded-xl p-8 text-center" data-testid="fines-empty">No fines logged{season ? ` for ${season}` : ""}.</div>}
      {data && data.items.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-widest uppercase opacity-60 text-left"><tr><th className="p-2">Logged</th><th className="p-2">Team</th><th className="p-2">Reason</th><th className="p-2">Payee / ref</th><th className="p-2 text-right">Amount</th><th className="p-2">Deadline</th><th className="p-2">Status</th><th className="p-2"></th></tr></thead>
            <tbody>
              {data.items.map(f => (
                <tr key={f.id} className="border-t border-border" data-testid={`fine-row-${f.id}`}>
                  <td className="p-2 whitespace-nowrap">{(f.created_at || "").slice(0, 10)}</td>
                  <td className="p-2">{f.team || "—"}</td>
                  <td className="p-2 max-w-xs truncate" title={f.reason}>{f.reason || "—"}</td>
                  <td className="p-2 text-xs">{f.payee || ""}{f.reference ? <div className="font-mono opacity-70">{f.reference}</div> : null}</td>
                  <td className="p-2 text-right font-semibold whitespace-nowrap">{gbp(f.amount)}</td>
                  <td className="p-2 whitespace-nowrap">{f.deadline || "—"}</td>
                  <td className="p-2"><span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_CLS[f.status]}`} data-testid={`fine-status-${f.id}`}>{f.status}</span>{f.payment_reference && <div className="text-[10px] opacity-60 font-mono">{f.payment_reference}</div>}</td>
                  <td className="p-2 whitespace-nowrap">
                    {f.status === "open" ? <>
                      <button onClick={() => setStatus(f, "paid")} className="text-xs text-[#83B68B] hover:underline mr-2" data-testid={`fine-mark-paid-${f.id}`}>Mark paid</button>
                      <button onClick={() => setStatus(f, "waived")} className="text-xs opacity-60 hover:underline" data-testid={`fine-waive-${f.id}`}>Waive</button>
                    </> : <button onClick={() => setStatus(f, "open")} className="text-xs opacity-60 hover:underline" data-testid={`fine-reopen-${f.id}`}>Reopen</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && <AddFineDialog onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </div>
  );
}

function Stat({ label, value, testid, accent }) {
  return <div className={`card-tactical rounded-md p-3 ${accent ? "border-amber-500/40" : ""}`} data-testid={testid}><div className="text-[10px] tracking-widest uppercase opacity-60">{label}</div><div className="font-display font-black text-2xl mt-1">{value}</div></div>;
}

function AddFineDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ amount: "", team: "", reason: "", payee: "", reference: "", deadline: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = () => api.post("/finance/fines", { ...f, deadline: f.deadline || null }).then(() => { toast.success("Fine logged"); onSaved(); }).catch(e => toast.error(formatApiError(e.response?.data?.detail)));
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="fine-add-dialog">
        <DialogHeader><DialogTitle>Log a league fine</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" step="0.01" placeholder="Amount £" value={f.amount} onChange={set("amount")} data-testid="fine-amount" />
          <Input placeholder="Team (e.g. U12 Palmer)" value={f.team} onChange={set("team")} data-testid="fine-team" />
          <Input className="col-span-2" placeholder="Reason" value={f.reason} onChange={set("reason")} data-testid="fine-reason" />
          <Input placeholder="Payee (league)" value={f.payee} onChange={set("payee")} data-testid="fine-payee" />
          <Input placeholder="Reference" value={f.reference} onChange={set("reference")} data-testid="fine-reference" />
          <Input type="date" value={f.deadline} onChange={set("deadline")} data-testid="fine-deadline" />
        </div>
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.amount} data-testid="fine-save">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
}
